
import { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tables } from "@/integrations/supabase/types";
import { useQuoteTracking } from "@/hooks/useQuoteTracking";
import { useActivityLogger } from "@/hooks/useActivityLogger";
import { useAuth } from "@/hooks/useAuth";
import { usePaymentFlowConfig } from "@/hooks/usePaymentFlowConfig";

type Quote = Tables<'quotes'>;

export const useQuoteResponseHandler = (bookingId: string, quotes: Quote[], onQuoteUpdate?: () => void) => {
  const [loadingQuoteId, setLoadingQuoteId] = useState<string | null>(null);
  const { trackQuoteView } = useQuoteTracking();
  const { logQuoteAccepted, logQuoteRejected } = useActivityLogger();
  const { user } = useAuth();
  const { shouldSkipPayment, paymentMode } = usePaymentFlowConfig();

  const handleQuoteResponse = async (quoteId: string, action: 'accepted' | 'rejected', response?: string) => {
    setLoadingQuoteId(quoteId);
    try {
      console.log('[QUOTE-RESPONSE] Starting quote response:', { quoteId, action, bookingId });
      
      // Track the quote view
      await trackQuoteView(quoteId);

      // Get quote details for logging
      const quote = quotes.find(q => q.id === quoteId);
      console.log('[QUOTE-RESPONSE] Found quote:', quote);

      // If accepting a quote, first reject all other quotes for this booking
      if (action === 'accepted') {
        console.log('[QUOTE-RESPONSE] Rejecting other quotes for booking:', bookingId);
        const { error: rejectError } = await supabase
          .from('quotes')
          .update({
            status: 'rejected',
            updated_at: new Date().toISOString()
          })
          .eq('booking_id', bookingId)
          .neq('id', quoteId)
          .eq('status', 'pending');

        if (rejectError) {
          console.error('[QUOTE-RESPONSE] Error rejecting other quotes:', rejectError);
        } else {
          console.log('[QUOTE-RESPONSE] Successfully rejected other quotes');
        }
      }

      // Update the selected quote status
      console.log('[QUOTE-RESPONSE] Updating quote status to:', action);
      const { data: updatedQuote, error: updateError } = await supabase
        .from('quotes')
        .update({
          status: action,
          updated_at: new Date().toISOString()
        })
        .eq('id', quoteId)
        .select()
        .single();

      if (updateError) {
        console.error('[QUOTE-RESPONSE] Error updating quote status:', updateError);
        toast.error(`Failed to update quote: ${updateError.message}`);
        return;
      }

      console.log('[QUOTE-RESPONSE] Quote status updated successfully:', updatedQuote);

      // Update the booking status using the proper status update function
      // This prevents check constraint violations by using the validated transitions
      if (action === 'accepted') {
        // Determine target status based on payment flow configuration
        const targetStatus = shouldSkipPayment ? 'booked' : 'quote_was_accepted';
        const statusNote = shouldSkipPayment 
          ? `Quote accepted - booking confirmed via ${paymentMode}`
          : 'Quote accepted by customer';
        
        console.log(`[QUOTE-RESPONSE] Updating booking status to ${targetStatus} (payment mode: ${paymentMode})`);
        
        const { data: statusResult, error: statusError } = await supabase.rpc('update_booking_status', {
          p_booking_id: bookingId,
          p_new_status: targetStatus,
          p_updated_by: null, // system update
          p_notes: statusNote
        });

        if (statusError) {
          console.error('[QUOTE-RESPONSE] Error updating booking status:', statusError);
          // Don't fail the entire operation if status update fails
          console.warn('[QUOTE-RESPONSE] Quote was updated but booking status update failed');
        } else {
          console.log('[QUOTE-RESPONSE] Booking status updated successfully:', statusResult);
        }
      } else if (action === 'rejected') {
        console.log('[QUOTE-RESPONSE] Updating booking status to quote_was_rejected');
        const { data: statusResult, error: statusError } = await supabase.rpc('update_booking_status', {
          p_booking_id: bookingId,
          p_new_status: 'quote_was_rejected',
          p_updated_by: null, // system update
          p_notes: 'Quote rejected by customer'
        });

        if (statusError) {
          console.error('[QUOTE-RESPONSE] Error updating booking status:', statusError);
          // Don't fail the entire operation if status update fails
          console.warn('[QUOTE-RESPONSE] Quote was updated but booking status update failed');
        } else {
          console.log('[QUOTE-RESPONSE] Booking status updated successfully:', statusResult);
        }
      }

      // Log the activity
      if (action === 'accepted') {
        await logQuoteAccepted(
          bookingId,
          user?.user_metadata?.name || user?.email,
          user?.email,
          quoteId,
          quote?.quoted_amount
        );
      } else if (action === 'rejected') {
        await logQuoteRejected(
          bookingId,
          user?.user_metadata?.name || user?.email,
          user?.email,
          quoteId,
          response
        );
      }

      toast.success(`Quote ${action} successfully`);
      console.log('[QUOTE-RESPONSE] Quote response completed successfully');
      
      onQuoteUpdate?.();
    } catch (error) {
      console.error('[QUOTE-RESPONSE] Error during quote response:', error);
      toast.error(`Failed to update quote: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoadingQuoteId(null);
    }
  };

  return {
    loadingQuoteId,
    handleQuoteResponse
  };
};
